import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Video, 
  VideoOff, 
  Camera,
  Play,
  Square,
  Users,
  MessageSquare,
  Hand,
  Settings,
  Maximize2,
  AlertCircle,
  Loader2,
  RotateCcw
} from 'lucide-react';
import Theta360Viewer from './Theta360Viewer';
import ThetaCameraSelector from './ThetaCameraSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AG360MeetingRoom - Meeting room component with 360° video support
 * Integrates RICOH THETA 360° camera with Daily.co video conferencing
 */
const AG360MeetingRoom = ({
  selectedAG,
  user,
  token,
  onStartAG,
  onError,
  isHost = false
}) => {
  // Room state
  const [roomMode, setRoomMode] = useState(null); // 'standard' | '360'
  const [isStreaming, setIsStreaming] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  
  // Camera state
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [is360Mode, setIs360Mode] = useState(false);
  
  // Daily.co state
  const [dailyFrame, setDailyFrame] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [participants, setParticipants] = useState([]);

  // Handle camera selection
  const handleCameraSelected = (cameraInfo) => {
    setSelectedCamera(cameraInfo);
    setIs360Mode(cameraInfo.isTheta);
  };

  // Handle stream ready from camera selector
  const handleStreamReady = (stream, isTheta) => {
    setCameraStream(stream);
    setIs360Mode(isTheta);
  };

  // Start AG with selected mode
  const handleStartAGWithMode = async (mode) => {
    setRoomMode(mode);
    setIsConnecting(true);

    try {
      // Create Daily.co room
      const roomName = `syndicappli-ag-${selectedAG.id}-${mode}`.replace(/_/g, '-').toLowerCase().substring(0, 40);
      
      const roomResponse = await axios.post(
        `${API}/daily/rooms`,
        { 
          room_name: roomName, 
          assemblee_id: selectedAG.id,
          room_type: mode // 'standard' or '360'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const roomUrl = roomResponse.data.room_url;
      
      // Update AG with room info
      await axios.put(
        `${API}/assemblees/${selectedAG.id}`,
        { 
          status: 'Ouverte',
          room_url: roomUrl,
          room_type: mode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsStreaming(true);
      onStartAG?.(mode, roomUrl);
      
    } catch (error) {
      console.error('Error starting AG:', error);
      onError?.('Erreur lors du démarrage de l\'AG');
    } finally {
      setIsConnecting(false);
    }
  };

  // Stop streaming
  const handleStopStreaming = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsStreaming(false);
    setRoomMode(null);
  };

  // Render setup panel for host
  const renderSetupPanel = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="ag-setup-panel">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Settings className="text-[#00D3A8]" size={20} />
        Configuration de la salle de réunion
      </h3>

      {/* Camera selector */}
      <div className="mb-6">
        <ThetaCameraSelector
          onCameraSelected={handleCameraSelected}
          onStreamReady={handleStreamReady}
        />
      </div>

      {/* Preview */}
      {cameraStream && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Aperçu</h4>
          <div className="relative rounded-lg overflow-hidden bg-gray-900 aspect-video">
            {is360Mode ? (
              <Theta360Viewer 
                stream={cameraStream}
                isActive={true}
                className="w-full h-full"
              />
            ) : (
              <video
                autoPlay
                muted
                playsInline
                ref={(el) => el && (el.srcObject = cameraStream)}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
              {is360Mode ? '360°' : 'Standard'}
            </div>
          </div>
        </div>
      )}

      {/* Start buttons */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">Démarrer l'Assemblée Générale</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Standard video button */}
          <button
            onClick={() => handleStartAGWithMode('standard')}
            disabled={isConnecting}
            className="flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all group"
            data-testid="btn-start-ag-standard"
          >
            <div className="p-4 bg-blue-100 rounded-full mb-3 group-hover:bg-blue-200 transition-colors">
              <Video className="text-blue-600" size={32} />
            </div>
            <span className="font-semibold text-gray-900">Démarrer l'AG</span>
            <span className="text-sm text-gray-500 mt-1">Visioconférence standard</span>
          </button>

          {/* 360° video button */}
          <button
            onClick={() => handleStartAGWithMode('360')}
            disabled={isConnecting || !is360Mode}
            className={`flex flex-col items-center p-6 rounded-xl border-2 transition-all group ${
              is360Mode
                ? 'border-gray-200 hover:border-[#00D3A8] hover:bg-[#00D3A8]/5'
                : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
            }`}
            data-testid="btn-start-ag-360"
          >
            <div className={`p-4 rounded-full mb-3 transition-colors ${
              is360Mode
                ? 'bg-[#00D3A8]/20 group-hover:bg-[#00D3A8]/30'
                : 'bg-gray-200'
            }`}>
              <Camera className={is360Mode ? 'text-[#00D3A8]' : 'text-gray-400'} size={32} />
            </div>
            <span className={`font-semibold ${is360Mode ? 'text-gray-900' : 'text-gray-400'}`}>
              Démarrer l'AG 360°
            </span>
            <span className={`text-sm mt-1 ${is360Mode ? 'text-gray-500' : 'text-gray-400'}`}>
              {is360Mode ? 'RICOH THETA connectée' : 'Connectez une RICOH THETA'}
            </span>
          </button>
        </div>

        {/* Connecting indicator */}
        {isConnecting && (
          <div className="flex items-center justify-center p-4 bg-[#00D3A8]/10 rounded-lg">
            <Loader2 size={20} className="animate-spin text-[#00D3A8] mr-2" />
            <span className="text-[#00D3A8] font-medium">Connexion en cours...</span>
          </div>
        )}

        {/* Help text */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h5 className="font-medium text-gray-800 mb-2">Comment utiliser la visio 360° ?</h5>
          <ol className="text-sm text-gray-600 space-y-1">
            <li>1. Connectez votre caméra RICOH THETA via USB</li>
            <li>2. Activez le mode "Live Streaming" sur la caméra</li>
            <li>3. Sélectionnez la caméra THETA dans la liste ci-dessus</li>
            <li>4. Cliquez sur "Démarrer l'AG 360°"</li>
          </ol>
          <p className="text-xs text-gray-500 mt-2">
            Compatible: RICOH THETA X, Z1, SC2
          </p>
        </div>
      </div>
    </div>
  );

  // Render streaming view
  const renderStreamingView = () => (
    <div className="space-y-4" data-testid="ag-streaming-view">
      {/* Video area */}
      <div className="relative rounded-xl overflow-hidden bg-gray-900" style={{ aspectRatio: '16/9' }}>
        {roomMode === '360' && cameraStream ? (
          <Theta360Viewer 
            stream={cameraStream}
            isActive={true}
            className="w-full h-full"
            showControls={true}
          />
        ) : (
          <iframe
            src={selectedAG.room_url 
              ? `${selectedAG.room_url}?name=${encodeURIComponent(`${user?.prenom || ''} ${user?.nom || ''}`.trim())}&showLeaveButton=true&showFullscreenButton=true`
              : ''
            }
            allow="camera; microphone; fullscreen; display-capture"
            className="w-full h-full"
            style={{ border: 'none' }}
          />
        )}

        {/* Mode badge */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${
            roomMode === '360' ? 'bg-[#00D3A8]' : 'bg-blue-500'
          }`}>
            {roomMode === '360' ? '360° THETA' : 'Standard'}
          </span>
          {isStreaming && (
            <span className="flex items-center gap-1 px-3 py-1 bg-red-500 rounded-full text-white text-sm">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              EN DIRECT
            </span>
          )}
        </div>

        {/* Host controls */}
        {isHost && (
          <div className="absolute bottom-4 right-4 flex items-center gap-2">
            <button
              onClick={handleStopStreaming}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              <Square size={18} />
              Arrêter la diffusion
            </button>
          </div>
        )}
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            <Users size={16} className="inline mr-1" />
            {participants.length} participant(s)
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowChat(!showChat)}
            className={`p-2 rounded-lg transition-colors ${
              showChat ? 'bg-[#00D3A8] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <MessageSquare size={20} />
          </button>
        </div>
      </div>
    </div>
  );

  // Render participant view (non-host)
  const renderParticipantView = () => {
    const is360Room = selectedAG?.room_type === '360';
    
    return (
      <div className="space-y-4" data-testid="ag-participant-view">
        {/* Info banner */}
        <div className={`p-4 rounded-lg ${is360Room ? 'bg-[#00D3A8]/10' : 'bg-blue-50'}`}>
          <div className="flex items-center gap-2">
            {is360Room ? (
              <Camera className="text-[#00D3A8]" size={20} />
            ) : (
              <Video className="text-blue-600" size={20} />
            )}
            <span className={`font-medium ${is360Room ? 'text-[#00D3A8]' : 'text-blue-700'}`}>
              {is360Room ? 'Salle de réunion 360°' : 'Salle de réunion'}
            </span>
          </div>
          {is360Room && (
            <p className="text-sm text-gray-600 mt-2">
              Utilisez votre souris ou vos doigts pour naviguer dans la vue 360°. 
              Zoomez avec la molette ou pincez l'écran.
            </p>
          )}
        </div>

        {/* Video area - will show Daily.co embed with 360° viewer overlay for THETA streams */}
        <div className="relative rounded-xl overflow-hidden bg-gray-900" style={{ aspectRatio: '16/9' }}>
          <iframe
            src={selectedAG.room_url 
              ? `${selectedAG.room_url}?name=${encodeURIComponent(`${user?.prenom || ''} ${user?.nom || ''}`.trim())}&showLeaveButton=true&showFullscreenButton=true`
              : ''
            }
            allow="camera; microphone; fullscreen; display-capture"
            className="w-full h-full"
            style={{ border: 'none' }}
          />
        </div>
      </div>
    );
  };

  // Main render
  if (isHost && !isStreaming) {
    return renderSetupPanel();
  }
  
  if (isStreaming) {
    return renderStreamingView();
  }
  
  return renderParticipantView();
};

export default AG360MeetingRoom;
